// src/features/analytics/components/AnalyticsAudiencePage.tsx

import { Suspense, lazy, useState } from 'react';
import { Group, SegmentedControl, Skeleton, Stack, Text } from '@mantine/core';
import { useNavigate } from '@tanstack/react-router';
import {
  ANALYTICS_GEO_DIMENSIONS,
  ANALYTICS_METRICS,
  ANALYTICS_METRIC_LABELS,
  ANALYTICS_SOURCE_DIMENSIONS,
  ANALYTICS_TECH_DIMENSIONS,
  type AnalyticsDimension,
  type AnalyticsGeoDimension,
  type AnalyticsMetric,
} from '@/lib/constants/analytics';
import { FEATURE_FLAGS } from '@/lib/constants/featureFlags';
import { HEATMAP_MAX_RANGE_DAYS, clampRangeSpan, rangeDays } from '@/shared/utils/utcRange';
import { AnalyticsUtils } from '../AnalyticsUtils';
import { DIMENSION_LABELS } from '../audienceValues';
import { useAnalyticsAudience } from '../useAnalyticsAudience';
import { useAnalyticsHeatmap } from '../useAnalyticsHeatmap';
import { useAnalyticsRange } from '../useAnalyticsRange';
import { useAnalyticsScope } from '../useAnalyticsScope';
import { ANALYTICS_PRESET_LABELS, type AnalyticsSearch } from '../searchParams';
import { AudienceBreakdownPanel } from './AudienceBreakdownPanel';
import { ConcurrencyHeatmapCard } from './ConcurrencyHeatmapCard';

// The map panel (d3-geo + vendored topojson/GeoNames chunks) loads only when
// a user opens the Map view — §4.2's entry-bundle gate.
const AudienceMapPanel = lazy(() => import('../map/AudienceMapPanel'));

const AUDIENCE_EMPTY_MESSAGE =
  'No playback analytics for this range. Playback analytics begins with the new player rollout.';

/**
 * One labeled group of the two-group dimension selector (plan §4.4:
 * Geography = Country | Region | City, Technology = Device | Platform |
 * Stream type). Both groups drive the single `dimension` URL param; the
 * group not holding the active dimension shows no selection.
 */
function DimensionGroup({
  label,
  dimensions,
  active,
  onChange,
}: {
  label: string;
  dimensions: readonly AnalyticsDimension[];
  active: AnalyticsDimension;
  onChange: (value: AnalyticsDimension) => void;
}) {
  const holdsActive = dimensions.includes(active);
  return (
    <Stack gap={4}>
      <Text size="xs" c="dimmed" tt="uppercase" fw={500}>
        {label}
      </Text>
      <SegmentedControl
        size="xs"
        data={dimensions.map((d) => ({ value: d, label: DIMENSION_LABELS[d] }))}
        value={holdsActive ? active : ''}
        onChange={(value) => value && onChange(value as AnalyticsDimension)}
      />
    </Stack>
  );
}

/** Account scope only (the layout hides/bounces this tab for network scope). */
export function AnalyticsAudiencePage() {
  const { search, range } = useAnalyticsRange();
  const { scope } = useAnalyticsScope(search.scope);
  const navigate = useNavigate();
  const accountCid = scope?.kind === 'account' ? scope.cid : null;

  // The dimension lives in the URL (§4.4) so audience views stay shareable;
  // absent means the default country. The metric toggle stays local state.
  const dimension: AnalyticsDimension = search.dimension ?? 'country';
  const setDimension = (value: AnalyticsDimension) => {
    void navigate({
      to: '.',
      search: (prev) => ({
        ...(prev as AnalyticsSearch),
        dimension: value === 'country' ? undefined : value,
      }),
    });
  };
  const [metric, setMetric] = useState<AnalyticsMetric>('watchTime');

  // Map presentation applies to geo dimensions only (plan §4.1). The param
  // survives a hop through a technology dimension (list renders meanwhile).
  const isGeoDimension = (ANALYTICS_GEO_DIMENSIONS as readonly AnalyticsDimension[]).includes(
    dimension
  );
  const view = isGeoDimension && search.view === 'map' ? 'map' : 'list';
  const setView = (value: string) => {
    void navigate({
      to: '.',
      search: (prev) => ({
        ...(prev as AnalyticsSearch),
        view: value === 'map' ? 'map' : undefined,
      }),
    });
  };
  const rangeLabel =
    search.preset === 'custom'
      ? AnalyticsUtils.formatUtcDayRangeLabel(range)
      : ANALYTICS_PRESET_LABELS[search.preset];

  const { audience, isLoading, isError, error, refetch } = useAnalyticsAudience(
    accountCid,
    dimension,
    range,
    metric
  );

  // The heatmap source is minute-grained presence: gateway cap is 90 days.
  const heatmapRangeWasClamped = rangeDays(range) > HEATMAP_MAX_RANGE_DAYS;
  const heatmapRange = clampRangeSpan(range, HEATMAP_MAX_RANGE_DAYS);
  const heatmapQuery = useAnalyticsHeatmap(accountCid, heatmapRange);

  return (
    <Stack gap="md">
      <Group gap="lg" wrap="wrap" align="flex-end">
        <DimensionGroup
          label="Geography"
          dimensions={ANALYTICS_GEO_DIMENSIONS}
          active={dimension}
          onChange={setDimension}
        />
        <DimensionGroup
          label="Technology"
          dimensions={ANALYTICS_TECH_DIMENSIONS}
          active={dimension}
          onChange={setDimension}
        />
        {/* Source (embedDomain, metrics expansion) — non-geo, so the existing
            isGeoDimension rule keeps the map toggle hidden automatically. */}
        {FEATURE_FLAGS.analyticsExpansionEnabled && (
          <DimensionGroup
            label="Source"
            dimensions={ANALYTICS_SOURCE_DIMENSIONS}
            active={dimension}
            onChange={setDimension}
          />
        )}
        <Stack gap={4}>
          <Text size="xs" c="dimmed" tt="uppercase" fw={500}>
            Metric
          </Text>
          <SegmentedControl
            size="xs"
            data={ANALYTICS_METRICS.map((m) => ({ value: m, label: ANALYTICS_METRIC_LABELS[m] }))}
            value={metric}
            onChange={(value) => setMetric(value as AnalyticsMetric)}
          />
        </Stack>
        {isGeoDimension && (
          <Stack gap={4}>
            <Text size="xs" c="dimmed" tt="uppercase" fw={500}>
              View
            </Text>
            <SegmentedControl
              size="xs"
              data={[
                { value: 'list', label: 'List' },
                { value: 'map', label: 'Map' },
              ]}
              value={view}
              onChange={setView}
            />
          </Stack>
        )}
      </Group>

      {view === 'map' ? (
        <Suspense fallback={<Skeleton height={380} />}>
          <AudienceMapPanel
            dimension={dimension as AnalyticsGeoDimension}
            metric={metric}
            audience={audience}
            range={range}
            isLoading={isLoading}
            isError={isError}
            error={error}
            onRetry={refetch}
            emptyMessage={AUDIENCE_EMPTY_MESSAGE}
            rangeLabel={rangeLabel}
          />
        </Suspense>
      ) : (
        <AudienceBreakdownPanel
          dimension={dimension}
          metric={metric}
          audience={audience}
          range={range}
          isLoading={isLoading}
          isError={isError}
          error={error}
          onRetry={refetch}
          emptyMessage={AUDIENCE_EMPTY_MESSAGE}
        />
      )}

      <ConcurrencyHeatmapCard
        heatmap={heatmapQuery.heatmap}
        range={heatmapRange}
        isLoading={heatmapQuery.isLoading}
        isError={heatmapQuery.isError}
        error={heatmapQuery.error}
        onRetry={heatmapQuery.refetch}
        rangeWasClamped={heatmapRangeWasClamped}
      />
    </Stack>
  );
}
